import { createSlice } from '@reduxjs/toolkit';

import {
    createUser,
    fetchAdminRoleOptions,
    fetchAdminScheduleTypeOptions,
    fetchAdminUsers,
    mergeAdminUserUpdate,
    reinviteStaff,
    updateUser
} from './user-admin-api';
import type { AdminDirectoryOption, AdminUser } from '@/types/user-admin';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { logout } from '../auth/logout';

export interface UserAdminState {
    users: AdminUser[];
    total: number;
    page: number;
    limit: number;
    usersLoading: boolean;
    usersError: string | null;
    createUserLoading: boolean;
    createUserError: string | null;
    updateUserLoading: boolean;
    updateUserError: string | null;
    reinviteStaffLoading: boolean;
    reinviteStaffError: string | null;
    roleOptions: AdminDirectoryOption[];
    roleOptionsLoading: boolean;
    scheduleTypeOptions: AdminDirectoryOption[];
    scheduleTypeOptionsLoading: boolean;
}

const initialState: UserAdminState = {
    users: [],
    total: 0,
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
    usersLoading: false,
    usersError: null,
    createUserLoading: false,
    createUserError: null,
    updateUserLoading: false,
    updateUserError: null,
    reinviteStaffLoading: false,
    reinviteStaffError: null,
    roleOptions: [],
    roleOptionsLoading: false,
    scheduleTypeOptions: [],
    scheduleTypeOptionsLoading: false
};

const userAdminSlice = createSlice({
    name: 'userAdmin',
    initialState,
    reducers: {
        clearUserAdminError: (state) => {
            state.usersError = null;
            state.createUserError = null;
            state.updateUserError = null;
            state.reinviteStaffError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdminUsers.pending, (state) => {
                state.usersLoading = true;
                state.usersError = null;
            })
            .addCase(fetchAdminUsers.fulfilled, (state, action) => {
                state.usersLoading = false;
                state.users = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.limit = action.payload.limit;
                state.usersError = null;
            })
            .addCase(fetchAdminUsers.rejected, (state, action) => {
                state.usersLoading = false;
                state.usersError = action.payload ?? 'Failed to fetch users list';
            })
            .addCase(createUser.pending, (state) => {
                state.createUserLoading = true;
                state.createUserError = null;
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.createUserLoading = false;
                state.createUserError = null;
                state.users = [action.payload, ...state.users];
                state.total += 1;
            })
            .addCase(createUser.rejected, (state, action) => {
                state.createUserLoading = false;
                state.createUserError = action.payload ?? 'Failed to create user';
            })
            .addCase(updateUser.pending, (state) => {
                state.updateUserLoading = true;
                state.updateUserError = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.updateUserLoading = false;
                state.updateUserError = null;
                state.users = state.users.map((user) =>
                    user.id === action.payload.id ? mergeAdminUserUpdate(user, action.payload) : user
                );
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.updateUserLoading = false;
                state.updateUserError = action.payload ?? 'Failed to update user';
            })
            .addCase(reinviteStaff.pending, (state) => {
                state.reinviteStaffLoading = true;
                state.reinviteStaffError = null;
            })
            .addCase(reinviteStaff.fulfilled, (state) => {
                state.reinviteStaffLoading = false;
                state.reinviteStaffError = null;
            })
            .addCase(reinviteStaff.rejected, (state, action) => {
                state.reinviteStaffLoading = false;
                state.reinviteStaffError = action.payload ?? 'Failed to reinvite staff member';
            })
            .addCase(fetchAdminRoleOptions.pending, (state) => {
                state.roleOptionsLoading = true;
            })
            .addCase(fetchAdminRoleOptions.fulfilled, (state, action) => {
                state.roleOptionsLoading = false;
                state.roleOptions = action.payload;
            })
            .addCase(fetchAdminRoleOptions.rejected, (state) => {
                state.roleOptionsLoading = false;
            })
            .addCase(fetchAdminScheduleTypeOptions.pending, (state) => {
                state.scheduleTypeOptionsLoading = true;
            })
            .addCase(fetchAdminScheduleTypeOptions.fulfilled, (state, action) => {
                state.scheduleTypeOptionsLoading = false;
                state.scheduleTypeOptions = action.payload;
            })
            .addCase(fetchAdminScheduleTypeOptions.rejected, (state) => {
                state.scheduleTypeOptionsLoading = false;
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearUserAdminError } = userAdminSlice.actions;
export default userAdminSlice.reducer;
